import React, { useEffect, useState } from "react";
import {
    Input,
    Button,
    message,
    Modal,
    Select,
    Table,
    Tag,
    Space,
    Card,
    Typography,
    Form,
    Tooltip,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ReloadOutlined,
    ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Hexapi from "../../../utility/HexAPI/Hexapi";
import { showConfirm } from "../../../utility/showconfirm";
import ModalCloseButton from "../../../component/ModalCloseButton";

const { Text, Title } = Typography;

const emptyForm = {
    leadsourceid: undefined,
    subleadsource: "",
};

const SubLeadSourcePanel = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [subLeadSourceList, setSubLeadSourceList] = useState([]);
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const getSubLeadSourceList = async () => {
        setLoading(true);
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_SubLeadSourceMaster_GetList]`,
                queryArr: [],
            };
            const result = await Hexapi(obj);
            setSubLeadSourceList(result?.[""] || []);
        } catch {
            message.error("Failed to fetch sub lead sources");
        } finally {
            setLoading(false);
        }
    };

    const getLeadSourceOptions = async () => {
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_LeadSourceMaster_GetList]`,
                queryArr: [],
            };
            const result = await Hexapi(obj);
            const list = result?.[""] || [];
            setLeadSourceOptions(
                list.map((ls) => ({ label: ls.leadsource, value: ls.recid }))
            );
        } catch {
            message.error("Failed to fetch lead sources");
        }
    };

    useEffect(() => {
        getSubLeadSourceList();
        getLeadSourceOptions();
    }, []);

    const leadSourceNameById = (id) => {
        const found = leadSourceOptions.find((o) => o.value === id);
        return found ? found.label : "-";
    };

    // Open Modal for Add or Edit
    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            form.setFieldsValue({
                leadsourceid: item.leadsourceid,
                subleadsource: item.subleadsource,
            });
        } else {
            form.setFieldsValue(emptyForm);
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const isEdit = !!editingItem;

            const ok = await showConfirm({
                title: isEdit ? "Update Sub Lead Source?" : "Add Sub Lead Source?",
                content: isEdit
                    ? `Are you sure you want to update "${editingItem.subleadsource}"?`
                    : `Are you sure you want to add "${values.subleadsource}"?`,
                okText: isEdit ? "Update" : "Add",
                cancelText: "Cancel",
            });

            if (!ok) return;

            setLoading(true);

            if (isEdit) {
                const obj = {
                    query: `[dbo].[CRM_Proc_SubLeadSourceMaster_Update] @RecID='{0}', @LeadSourceID='{1}', @SubLeadSource='{2}'`,
                    queryArr: [
                        `${editingItem.recid}`,
                        `${values.leadsourceid}`,
                        `${values.subleadsource}`,
                    ],
                };
                const res = await Hexapi(obj);
                if (res?.[""]?.[0]?.updatedrecid !== undefined) {
                    message.success("Sub lead source updated successfully");
                    setIsModalOpen(false);
                    getSubLeadSourceList();
                } else {
                    message.error("Update failed");
                }
            } else {
                const obj = {
                    query: `[dbo].[CRM_Proc_SubLeadSourceMaster_Insert] @LeadSourceID='{0}', @SubLeadSource='{1}'`,
                    queryArr: [`${values.leadsourceid}`, `${values.subleadsource}`],
                };
                const res = await Hexapi(obj);
                if (res?.[""]?.[0]?.command === 1) {
                    message.success("Sub lead source added successfully");
                    setIsModalOpen(false);
                    getSubLeadSourceList();
                } else {
                    message.error("Add failed");
                }
            }
        } catch (err) {
            if (err?.errorFields) return;
            message.error("Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (item) => {
        const ok = await showConfirm({
            title: `Delete "${item.subleadsource}"?`,
            content: `This action cannot be undone. "${item.subleadsource}" will be permanently removed.`,
            okText: "Delete",
            cancelText: "Cancel",
            danger: true,
        });

        if (!ok) return;

        setLoading(true);
        try {
            const obj = {
                query: `[dbo].[CRM_Proc_SubLeadSourceMaster_Delete] @RecID='{0}'`,
                queryArr: [`${item.recid}`],
            };
            await Hexapi(obj);
            message.success(`"${item.subleadsource}" deleted successfully`);
            getSubLeadSourceList();
        } catch {
            message.error("Delete failed");
        } finally {
            setLoading(false);
        }
    };

    const filteredSubLeadSources = subLeadSourceList.filter((s) =>
        (s.subleadsource || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
        {
            title: "Sub Lead Source",
            dataIndex: "subleadsource",
            key: "subleadsource",
            sorter: (a, b) => (a.subleadsource || "").localeCompare(b.subleadsource || ""),
            render: (text) => (
                <Space size={12}>
                    <div
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            backgroundColor: "#1250AC",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 12,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                    >
                        {(text || "").charAt(0).toUpperCase()}
                    </div>
                    <Text strong style={{ fontSize: 14 }}>{text}</Text>
                </Space>
            ),
        },
        {
            title: "Lead Source",
            dataIndex: "leadsourceid",
            key: "leadsourceid",
            width: 220,
            filters: leadSourceOptions.map((o) => ({ text: o.label, value: o.value })),
            onFilter: (value, record) => record.leadsourceid === value,
            render: (id) => (
                <Tag color="blue" style={{ borderRadius: 12, padding: "0 10px" }}>
                    {leadSourceNameById(id)}
                </Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 110,
            align: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Edit Sub Lead Source">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Sub Lead Source">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, width: "100%", height: "calc(100vh - 48px)", display: "flex", flexDirection: "column" }}>
            <Card
                bordered={false}
                style={{
                    borderRadius: 16,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                }}
                styles={{
                    body: {
                        padding: 24,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Header controls */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 20,
                        gap: 16,
                        flexWrap: "wrap",
                        flexShrink: 0,
                    }}
                >
                    <Space size={12}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                padding: 0,
                            }}
                        />
                        <Title level={4} style={{ margin: 0 }}>
                            Sub Lead Source Master
                        </Title>
                        <Tag style={{ borderRadius: 10, padding: "0 8px" }}>
                            {filteredSubLeadSources.length} total
                        </Tag>
                    </Space>

                    <Space size={12} style={{ flexWrap: "wrap" }}>
                        <Input
                            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                            placeholder="Search sub lead sources…"
                            allowClear
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: 240, borderRadius: 8 }}
                        />
                        <Tooltip title="Reload Sub Lead Sources">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={getSubLeadSourceList}
                                loading={loading}
                                style={{ borderRadius: 8 }}
                            />
                        </Tooltip>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal(null)}
                            style={{ borderRadius: 8 }}
                        >
                            Add Sub Lead Source
                        </Button>
                    </Space>
                </div>

                {/* Main Data Table with Vertical Scrolling */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                    <Table
                        columns={columns}
                        dataSource={filteredSubLeadSources}
                        rowKey="recid"
                        loading={loading}
                        scroll={{ y: "calc(100vh - 280px)" }}
                        pagination={false}
                    />
                </div>
            </Card>

            {/* Combined Add/Edit Modal */}
            <Modal
                centered
                title={null}
                closable={false}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleSave}
                okText={editingItem ? "Update" : "Add"}
                confirmLoading={loading}
                width={440}
                destroyOnClose
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                    }}
                >
                    <Title level={5} style={{ margin: 0 }}>
                        {editingItem ? "Edit Sub Lead Source" : "Add Sub Lead Source"}
                    </Title>
                    <ModalCloseButton onClose={() => setIsModalOpen(false)} />
                </div>
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="leadsourceid"
                        label="Lead Source"
                        rules={[{ required: true, message: "Please select a lead source" }]}
                    >
                        <Select
                            placeholder="Select lead source"
                            options={leadSourceOptions}
                            showSearch
                            optionFilterProp="label"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="subleadsource"
                        label="Sub Lead Source Name"
                        rules={[{ required: true, message: "Please enter sub lead source name" }]}
                    >
                        <Input placeholder="e.g. Instagram Story Ad" style={{ borderRadius: 8 }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubLeadSourcePanel;